import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable, map} from 'rxjs';
import {BaseService} from './_base.service';
import { TopicId, UUID } from '../models/uuid';
import { Topic, TopicConfig } from '../models/topic';
import { MotiGrantee } from '../models/moti-fga/moti-grantee';
import { FgaType } from '../models/moti-fga/moti-type';


interface Query {
  topicIds?: TopicId[] | string,
  userId?: UUID,
  objectId?: string,
  objectType?: string,
}


@Injectable({
  providedIn: 'root',
})
export class TopicService extends BaseService {


  constructor(
    private http: HttpClient,
  ) {
    super();
    // this.enableHttpLocalhost();
  }


  getTopic$(topicId: TopicId): Observable<Topic> {
    return new Observable<Topic>(subscriber => {
      this.queryTopics$({topicIds: [topicId]}).subscribe({
        next: topics => {
          if (topics.length === 1 && topics[0].id === topicId) {
            subscriber.next(topics[0]);
          } else {
            subscriber.next(null);
          }

          subscriber.complete();
        },
        error: err => {
          subscriber.error(err);
        },
      });
    });
  }


  queryTopics$(query: Query): Observable<Topic[]> {
    for (let key in query) {
      let val = query[key as keyof Query];

      if (Array.isArray(val)) {
        val = val.join(',');
      }
    }

    return this.http.get<Topic[]>(`${this.httpHost}/topics`, {
      params: query as HttpParams,
    });
  }


  patchTopic$(topicId: TopicId, patch: {
    name?: string,
    description?: string,
    config?: TopicConfig,
    dataSourceIds?: UUID[],
  }): Observable<any> {
    return this.http.patch(`${this.httpHost}/topics/${topicId}`, patch);
  }


  /**
   * Queries for an existing topic for a specific object
   * @param objectId - The ID of the object to find a topic for
   * @param objectType - The type of the object (app, device, hub, tile, user)
   * @returns Observable with the found topic or null if none exists
   */
  findTopicForObject$(objectId: string, objectType: string): Observable<Topic | null> {
    return this.queryTopics$({ objectId, objectType }).pipe(
      map(topics => topics.length > 0 ? topics[0] : null)
    );
  }

  /**
   * Creates a topic for an object based on the API contract
   * @param objectId - The ID of the object to create a topic for
   * @param objectType - The type of the object (app, device, hub, tile, user)
   * @returns Observable with the created topic or error with existing topic in 409 response
   */
  createTopicForObject$(objectId: string, objectType: string): Observable<Topic> {
    const requestBody = {
      objectId: objectId,
      objectType: objectType
    };

    return this.http.post<Topic>(`${this.httpHost}/topics`, requestBody);
  }

  createTopic$(grantee: MotiGrantee, topic: Topic): Observable<Topic> {
    let params = new HttpParams();

    switch (grantee.type) {
      case FgaType.team:
        params = params.set('teamId', grantee.id);
        break;
      case FgaType.user:
        params = params.set('userId', grantee.id);
        break;
      default:
        throw new Error('invalid grantee type for topic');
    }

    return new Observable<Topic>(subscriber => {
      this.http.post<Topic>(`${this.httpHost}/topics`, topic, {
        params,
      }).subscribe({
        next: topic => {
          subscriber.next(topic);
        },
        error: err => {
          subscriber.error(err);
        },
        complete: () => {
          subscriber.complete();
        },
      });
    });
  }


  deleteTopic$(topicId: TopicId): Observable<any> {
    return this.http.delete(`${this.httpHost}/topics/${topicId}`);
  }


  addDataSource$(topicId: TopicId, dataSourceId: UUID): Observable<any> {
    return this.http.post<any>(`${this.httpHost}/topics/${topicId}/data-sources/${dataSourceId}`, null);
  }


  removeDataSource$(topicId: TopicId, dataSourceId: UUID): Observable<any> {
    return this.http.delete(`${this.httpHost}/topics/${topicId}/data-sources/${dataSourceId}`);
  }


  setPrompt$(topicId: TopicId, promptId: UUID) {
    return this.http.put(`${this.httpHost}/topics/${topicId}/prompts/${promptId}`, null);
  }


  removePrompt$(topicId: TopicId) {
    return this.http.delete(`${this.httpHost}/topics/${topicId}/prompts`);
  }
}
